import { useMemo } from 'react';
import {
  Accordion,
  Box,
  Checkbox,
  Flex,
  AccordionItem,
  AccordionButton,
  AccordionIcon,
  AccordionPanel,
  Spinner,
  Icon,
} from '@chakra-ui/react';
import { components, type MenuProps } from 'react-select';
import { Users03 } from '@kula-ai/icons/dual-tone/Users03';
import { KText } from '@/ui-library/components';

interface Option {
  value: string;
  label: string;
  room_name: string;
  capacity: number;
  available: boolean;
}

interface AccordionOption {
  id: string;
  value: string;
  label: string;
  name: string;
  capacity: number;
  available: boolean;
}

interface AccordionGroup {
  title: string;
  count: number;
  options: AccordionOption[];
}

interface OptionGroup {
  label: string;
  options: Option[];
}

export const SchMeetingRoomMenu = (props: MenuProps<Option, true>) => {
  const { selectProps } = props;
  const accordionGroups = selectProps.accordionGroups as AccordionGroup[];
  const selectedValues = selectProps.value as Option[];
  const onChange = selectProps.onChange as (options: Option[]) => void;
  const isMulti = selectProps.isMulti as boolean;
  const inputValue = selectProps.inputValue as string;
  const isLoading = selectProps.isLoading as boolean;

  const handleOptionSelect = (option: AccordionOption) => {
    let newSelectedOptions: Option[] = [];

    // Find the original option with full data from the select options
    let selectedOption: Option | undefined;
    for (const group of selectProps.options as OptionGroup[]) {
      selectedOption = group.options.find((opt) => opt.value === option.value);
      if (selectedOption) break;
    }

    if (!selectedOption) return;

    if (isMulti) {
      // For multi-select, toggle the selection
      const isSelected = selectedValues.some(
        (opt) => opt.value === option.value
      );
      if (isSelected) {
        newSelectedOptions = selectedValues.filter(
          (opt) => opt.value !== option.value
        );
      } else {
        newSelectedOptions = [...selectedValues, selectedOption];
      }
    } else {
      // For single-select, replace the selection
      newSelectedOptions = [selectedOption];
    }

    onChange(newSelectedOptions);
    // Close the menu after selection
    if (!isMulti) selectProps.onMenuClose();
  };

  // Filter groups and their options based on input value
  const filteredGroups = useMemo(() => {
    if (!accordionGroups?.length) return [];
    if (!inputValue) return accordionGroups;

    return accordionGroups
      .map((group) => {
        // Filter the options in each group
        const filteredOptions = group.options.filter((option) =>
          option.name?.toLowerCase().includes(inputValue?.toLowerCase())
        );

        // Return the group with filtered options
        return {
          ...group,
          options: filteredOptions,
          count: filteredOptions.length,
        };
      })
      .filter((group) => group.options.length > 0); // Only include groups with matching options
  }, [accordionGroups, inputValue]);

  // Custom item renderer with a checkbox
  const CustomItemRenderer = ({ option, isSelected }) => {
    return (
      <Flex w="100%" justify="space-between" align="center">
        <Flex align="center" gap="10px">
          <Checkbox
            isChecked={isSelected}
            colorScheme="blue"
            pointerEvents="none"
          />
          <KText>{option.name}</KText>
        </Flex>
        <KText fontSize="xs" color="gray.500">
          {option.capacity}
        </KText>
      </Flex>
    );
  };

  // Show loading state directly in the menu
  if (isLoading) {
    return (
      <components.Menu {...props}>
        <Box
          minH="200px"
          display="flex"
          alignItems="center"
          justifyContent="center"
        >
          <Flex direction="column" align="center" gap="12px">
            <Spinner color="primary-800" size="lg" thickness="3px" />
            <KText color="secondary-700">Loading meeting rooms...</KText>
          </Flex>
        </Box>
      </components.Menu>
    );
  }

  return (
    <components.Menu {...props}>
      <Box maxH="300px" overflowY="auto">
        <Accordion
          allowMultiple
          defaultIndex={filteredGroups.map((_, idx) => idx)}
        >
          {filteredGroups.map((group) => {
            const options = group.options.map((option) => {
              const isSelected = selectedValues.some(
                (val) => val.value === option.value
              );
              return {
                ...option,
                renderContent: (
                  <CustomItemRenderer option={option} isSelected={isSelected} />
                ),
              };
            });

            return (
              <AccordionItem key={group.title}>
                <AccordionButton
                  _hover={{ bg: '#F1F4F9' }}
                  borderBottom="1px solid #E4E5FA"
                  bg="#FAFBFD"
                  py={0}
                  _focus={{ border: 'transparent' }}
                >
                  <Flex gap="10px">
                    <Box p="12px 7px">
                      <AccordionIcon />
                    </Box>
                    <Box borderLeft="1px solid #E4E5FA" />
                    <KText variant="heading_2" p="14px 7px" size="sm-medium">
                      {group.title || 'No location'}
                    </KText>
                    <Box
                      bg="#FFFFFF"
                      border="1px solid #DBDBDB"
                      borderRadius="6px"
                      p="2px 8px"
                      my="12px"
                    >
                      <KText>{group.count}</KText>
                    </Box>
                  </Flex>
                </AccordionButton>
                <AccordionPanel py={0} px={0}>
                  {options.length > 0 ? (
                    options.map((option, index) => {
                      const isSelected = selectedValues.some(
                        (val) => val.value === option.value
                      );

                      return (
                        <Box
                          key={option.id}
                          cursor="pointer"
                          borderBottom={
                            index < options.length - 1
                              ? '1px solid #EBF2F5'
                              : 'none'
                          }
                          onClick={() => handleOptionSelect(option)}
                          bg={
                            isSelected
                              ? 'rgba(13, 110, 253, 0.05)'
                              : 'transparent'
                          }
                          _hover={{ bg: 'rgba(13, 110, 253, 0.05)' }}
                        >
                          <Flex
                            px="24px"
                            py="10px"
                            alignItems="center"
                            justifyContent="space-between"
                            gap="6px"
                          >
                            <Flex gap="10px" alignItems="flex-start">
                              {isMulti && (
                                <Checkbox
                                  isChecked={isSelected}
                                  colorScheme="blue"
                                  pointerEvents="none"
                                  mt="3px"
                                />
                              )}
                              <Flex flexDir="column" gap="5px">
                                <Flex align="center" gap="4px">
                                  <KText>{option.name}</KText>
                                  {option.available === false && (
                                    <Box
                                      w="8px"
                                      h="8px"
                                      borderRadius="full"
                                      bg="#C03744"
                                    />
                                  )}
                                </Flex>
                                <Flex align="center" gap="4px">
                                  <Icon
                                    as={Users03}
                                    w="16px"
                                    h="16px"
                                    color="secondary-700"
                                  />
                                  <KText fontSize="xs" color="secondary-900">
                                    {option.capacity}
                                  </KText>
                                </Flex>
                              </Flex>
                            </Flex>
                          </Flex>
                        </Box>
                      );
                    })
                  ) : (
                    <Box textAlign="center" py="8px">
                      <KText color="secondary-900">
                        No meeting rooms found
                      </KText>
                    </Box>
                  )}
                </AccordionPanel>
              </AccordionItem>
            );
          })}
          {filteredGroups.length === 0 && (
            <Box textAlign="center" py="16px">
              <KText color="secondary-900">
                No matching meeting rooms found
              </KText>
            </Box>
          )}
        </Accordion>
      </Box>
    </components.Menu>
  );
};
